import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { Control, Controller, FieldValues, Path } from "react-hook-form";

import { useGetAdvertisements } from "@/api/advertisement.api";

interface CryptorAdvertisementsFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  title: string;
}

// Ordered multi-select of ads. The form value is an ordered list of ad ids;
// that order is the in-app playback order. Use the Select to add/remove and the
// arrows to reorder.
const CryptorAdvertisementsField = <T extends FieldValues>({
  control,
  name,
  title,
}: CryptorAdvertisementsFieldProps<T>) => {
  const { data: advertisements } = useGetAdvertisements();
  const ads = advertisements || [];
  const adById = (id: number) => ads.find((ad) => ad.id === id);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => {
        const value: number[] = (field.value as number[]) || [];

        const move = (index: number, direction: -1 | 1) => {
          const target = index + direction;
          if (target < 0 || target >= value.length) return;
          const next = [...value];
          [next[index], next[target]] = [next[target], next[index]];
          field.onChange(next);
        };

        const remove = (id: number) =>
          field.onChange(value.filter((current) => current !== id));

        return (
          <FormControl margin="normal" fullWidth>
            <InputLabel id={`${name}-label`}>{title}</InputLabel>
            <Select
              labelId={`${name}-label`}
              label={title}
              multiple
              value={value}
              onChange={(e) => field.onChange(e.target.value as number[])}
              renderValue={(selected) =>
                (selected as number[])
                  .map((id) => adById(id)?.title ?? `#${id}`)
                  .join(", ")
              }
            >
              {ads.map((ad) => (
                <MenuItem value={ad.id} key={ad.id}>
                  {ad.title}
                </MenuItem>
              ))}
            </Select>
            {value.length > 0 && (
              <Stack gap={1} mt={1}>
                {value.map((id, index) => {
                  const ad = adById(id);
                  return (
                    <Box
                      key={id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ border: "1px solid #ddd", borderRadius: 1, p: 1 }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography fontWeight="bold">{index + 1}.</Typography>
                        {ad?.image?.url && (
                          <img src={ad.image.url} alt={ad?.title} width={40} />
                        )}
                        <Typography>{ad?.title ?? `#${id}`}</Typography>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          disabled={index === 0}
                          onClick={() => move(index, -1)}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={index === value.length - 1}
                          onClick={() => move(index, 1)}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => remove(id)}
                        >
                          Retirer
                        </Button>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default CryptorAdvertisementsField;
